export function extractTextFromHTML(html: string): string {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  return doc.body.textContent ?? ''
}

const BLOCK_ELEMENTS =
  'p,div,section,article,header,footer,aside,main,nav,pre,blockquote,ul,ol,li,dl,dt,dd,table,thead,tbody,tfoot,tr,td,th,hr,form,fieldset,legend,details,summary,figure,figcaption,h1,h2,h3,h4,h5,h6'

/* Visible text split into one entry per block, blank blocks dropped.
 *
 * "Line" here means a block-level node — a paragraph, heading or list item —
 * not a `\n` character, because our note content is TipTap/ProseMirror output
 * where newlines are structure rather than text.
 *
 * The sidebar row splits a note into a title (the first line of actual
 * content) and a preview (everything after it, reflowed), so both have to come
 * from one tokenization; deriving them separately would let the preview repeat
 * or drop a line.
 *
 * Unlike `extractTextFromHTML`, block boundaries survive: `textContent`
 * concatenates sibling blocks with no separator, so `<p>hello</p><p>yay</p>`
 * reads back as the single token `helloyay`, which no full-text query for
 * either word can match. Consumers that index a whole document should join
 * these lines with whitespace. */
export function extractBlockLinesFromHTML(html: string): string[] {
  return (
    htmlLines(html)
      // Collapse each line's internal whitespace, then drop the blank ones —
      // this is what skips leading empty paragraphs so the title is the first
      // line that actually has text.
      .map((line) => line.replace(/\s+/g, ' '))
      .filter(Boolean)
  )
}

export function extractTitleFromHTML(html: string): string {
  const text = extractTextFromHTML(html)
  return text.length > 500 ? `${text.substring(0, 500).trim()}…` : text
}

// The document's text split at every line break a reader would see: <br> and
// each block boundary. `textContent` alone concatenates blocks with no
// separator, so "<p>a</p><p>b</p>" would read as "ab". Lines are trimmed but
// blanks are kept — each caller decides how to drop them.
function htmlLines(html: string): string[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  doc.body.querySelectorAll('br').forEach((br) => {
    br.replaceWith('\n')
  })

  let isFirstBlock = true
  doc.body.querySelectorAll(BLOCK_ELEMENTS).forEach((el) => {
    if (!isFirstBlock) {
      el.insertAdjacentText('beforebegin', '\n')
    }
    isFirstBlock = false
  })

  return (doc.body.textContent ?? '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
}

/**
 * Readable plain text for an HTML fragment, keeping its line structure. Use
 * over `extractTextFromHTML` whenever the result is shown to a person or copied
 * to the clipboard; that one is only safe for single-block content.
 * @param html - The HTML fragment to flatten.
 * @returns The text, one line per block, with blank lines removed.
 */
export function extractLinesFromHTML(html: string): string {
  return htmlLines(html)
    .filter((line) => line !== '')
    .join('\n')
}

export function extractFirstLineFromHTML(html: string): string {
  // Ignore blank or 1-char noise lines when picking the representative line.
  return htmlLines(html).filter((line) => line.length > 1)[0] ?? ''
}

export function stripEmptyEdgeParagraphs(html: string): string {
  return html
    .replace(/^(<p>(\s|<br\/?>)*<\/p>)+/, '')
    .replace(/(<p>(\s|<br\/?>)*<\/p>)+$/, '')
}

export function areHtmlEqual(html1: string, html2: string): boolean {
  const parser = new DOMParser()

  const doc1 = parser.parseFromString(html1, 'text/html')
  const doc2 = parser.parseFromString(html2, 'text/html')

  return doc1.body.isEqualNode(doc2.body)
}
